import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { blockLacksCropSectionMaker } from 'App/Map/PanelDetails/_utils/blockLacksCropSectionMaker'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { DropdownSelectorBlock } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorBlock/DropdownSelectorBlock'
import type { StackedChartSection } from 'components/StackedChart/types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { doesSelectedBlockHaveValueTypes } from 'utils/doesSelectedFieldAssetHaveValueTypes'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { PowderyMildewTooltip } from '../../../../../_utils/by-domain/powderyMildew/PowderyMildewTooltip'
import type { TPowderyMildewPlotLine } from '../../_utils/powderyMildewChartHelper'
import { secondaryInfectionRiskChartHelper } from '../../_utils/powderyMildewChartHelper'

type TValueTypeKey = 'cherryPowderyMildewModelPredictedRisk'

const preferredAggregationInterval = { preferredAggregationInterval: 'DAILY' } as const

const getValuesRequestedForBlocks = ({
  selectedFieldAssets,
  selectedValueGroups,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!selectedFieldAssets.block) return {}

  let valuesRequested: Partial<
    Record<
      VV.DomainTypes.CherryPowderyMildew.TTimeseriesValueTypeKeysMerged,
      typeof preferredAggregationInterval
    >
  > = {}

  Object.entries(selectedValueGroups).forEach(([valueGroup, isActive]) => {
    if (!!isActive && valueGroup.includes('cherry_powdery_mildew')) {
      if (!selectedPropertyHasPermission({ permission: 'VIEW_CHERRY_POWDERY_MILDEW_DETAILS' })) return

      valuesRequested = {
        cherryPowderyMildewModelPredictedRisk: preferredAggregationInterval,
      }
    }
  })

  if (Object.keys(valuesRequested).length === 0) return {}

  const valuesToBeRequested: Partial<routes.Values.Request> = {}

  if (
    Object.keys(valuesRequested).length &&
    doesSelectedBlockHaveValueTypes({ valuesTimeseries: Object.keys(valuesRequested) as TValueTypeKey[] })
  )
    valuesToBeRequested.blocks = {
      blockIds: [selectedFieldAssets.block],
      valuesRequested,
    }

  return valuesToBeRequested
}

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}) => {
  const blockValues = getValuesRequestedForBlocks({ selectedFieldAssets, selectedValueGroups })

  return { ...blockValues }
}

export const content = ({
  data,
  compareSeasonsData,
}: {
  data: routes.Values.Response
  compareSeasonsData: routes.Values.Response
}): StackedChartSection => {
  const { compareSeasonsInterval } = detailsPanelStore.getState()
  const selectedFieldAssets = selectedFieldAssetsStore.getState()
  const selectedProperty = selectedFieldAssets.property
  const blockId = String(selectedFieldAssets.block)
  const timezone = getTimezoneForSelectedPropertyOrRegion()

  const hasPermission = selectedPropertyHasPermission({
    permission: 'VIEW_CHERRY_POWDERY_MILDEW_DETAILS',
  })

  const valueTypeKey = 'cherryPowderyMildewModelPredictedRisk'

  const commonReturnItems = {
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{unitConverter.cherryPowderyMildew(null).titleWithoutUnit()}</span>
        <PowderyMildewTooltip crop={'cherry'} />
      </div>
    ),
    fieldAssetPicker: (
      <DropdownSelectorBlock valuesTimeseriesToFilterOn={[valueTypeKey]} showAssociatedWeatherStationName />
    ),
    id: `stackem-cherry-powdery-mildew-risk-${blockId}`,
  }

  if (!hasPermission && selectedProperty) {
    return propertyLacksPermissionSectionMaker(commonReturnItems)
  }

  const blockHasValueTypes = doesSelectedBlockHaveValueTypes({ valuesTimeseries: [valueTypeKey] })

  if (!blockHasValueTypes && selectedProperty) {
    return blockLacksCropSectionMaker({
      ...commonReturnItems,
      translatedCropName: translate.phrases.dbCropsName('cherry'),
    })
  }

  const allForecastMoments = [
    data?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.metadata?.forecastStartsAt,
  ]
    .filter(Boolean)
    .map((d) => moment.tz(d, timezone))

  const firstForecastTimestamp = allForecastMoments.length ? +moment.min(allForecastMoments) : +new Date()
  const plotLines: TPowderyMildewPlotLine[] = []
  const metadata = data?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.metadata

  if (metadata) {
    const { budbreakDates, modelPrimaryInfectionDates } = metadata

    budbreakDates.forEach((date) => {
      plotLines.push({
        id: `budbreak-${date}`,
        value: +new Date(date),
        label: translate.phrases.banyanApp('Bud Burst Date'),
      })
    })

    modelPrimaryInfectionDates.forEach((date) => {
      plotLines.push({
        id: `modelPrimaryInfection-${date}`,
        value: +new Date(date),
        label: translate.phrases.banyanApp('Model Predicted Primary Infection Date'),
      })
    })
  }

  const compareSeasonsMetadata = compareSeasonsData?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.metadata

  if (compareSeasonsMetadata) {
    const { budbreakDates, modelPrimaryInfectionDates } = compareSeasonsMetadata

    budbreakDates.forEach((date) => {
      plotLines.push({
        id: `compareSeasons-budbreak-${date}`,
        value: +moment.tz(date, timezone).add(compareSeasonsInterval, 'years'),
        label: translate.phrases.banyanApp('Bud Burst Date'),
        isCompareSeasons: true,
      })
    })

    modelPrimaryInfectionDates.forEach((date) => {
      plotLines.push({
        id: `compareSeasons-modelPrimaryInfection-${date}`,
        value: +moment.tz(date, timezone).add(compareSeasonsInterval, 'years'),
        label: translate.phrases.banyanApp('Model Predicted Primary Infection Date'),
        isCompareSeasons: true,
      })
    })
  }

  const chartConfig = secondaryInfectionRiskChartHelper({
    data: (data?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.timeseries ?? []).map(
      (d: { timestamp: string; value: number | null }) => [
        +new Date(d.timestamp),
        unitConverter.cherryPowderyMildew(d.value).value(),
      ],
    ),
    compareSeasonsData: compareSeasonsInterval
      ? (compareSeasonsData?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.timeseries ?? []).map(
          (d: { timestamp: string; value: number | null }) => [
            +new Date(d.timestamp),
            unitConverter.cherryPowderyMildew(d.value).value(),
          ],
        )
      : undefined,
    compareSeasonsInterval,
    forecastStartsAt: firstForecastTimestamp,
    seriesId: 'cherryPowderyMildewModelPredictedRisk',
    seriesName: translate.phrases.banyanApp('Model Predicted Risk'),
    seriesTooltip: {
      valueDecimals: unitConverter.cherryPowderyMildew().defaultNumberOfDecimalPlaces(),
      valueSuffix: ` ${unitConverter.cherryPowderyMildew().suffix()}`,
    },
    plotLines,
  })

  return {
    ...commonReturnItems,
    items: [
      {
        chartConfig,
      },
    ],
  }
}
